/**
 * Admin Settings Page
 * 
 * Admin-only page for managing system-wide settings:
 * - Registration settings (mode, approved domains, email verification)
 * - Session settings (timeout, max users)
 * - User management (list users, change roles)
 */

import { useState, useEffect } from 'react';
import { 
  useSystemPreferences, 
  useUpdateSystemPreferences,
  useAddApprovedDomain,
  useRemoveApprovedDomain,
  useUsers,
  useUpdateUserRole,
  useEmailConfig,
  useUpdateEmailConfig,
  useSendTestEmail,
  useMcpTokens,
  useCreateMcpToken,
  useRevokeMcpToken,
} from '@/hooks/usePreferences';
import { useAuth } from '@/contexts/AuthContext';
import { getApiBaseUrl } from '@/lib/runtime-config';
import { 
  Settings, 
  Users, 
  Shield, 
  Globe, 
  Plus,
  X,
  CheckCircle,
  AlertCircle,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Send,
  Bot,
  Copy,
  Key,
  RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  PageHeader,
  StatsGrid,
  StatCard,
  LoadingState,
  ErrorState,
} from '../../components/common';

type RegistrationMode = 'open' | 'restricted' | 'invite_only';
type AdminSettingsTab = 'registration' | 'email' | 'mcp' | 'users';

function resolveMcpUrl() {
  const apiBaseUrl = getApiBaseUrl();
  if (apiBaseUrl) {
    return `${apiBaseUrl}/mcp`;
  }
  return `${window.location.origin}/mcp`;
}

export function AdminSettingsPage() {
  const { user } = useAuth();
  const { data: systemPrefs, isLoading: prefsLoading, error: prefsError, refetch, isFetching } = useSystemPreferences();
  const { data: users, isLoading: usersLoading } = useUsers();
  const updatePreferences = useUpdateSystemPreferences();
  const addDomain = useAddApprovedDomain();
  const removeDomain = useRemoveApprovedDomain();
  const updateRole = useUpdateUserRole();
  
  const [registrationMode, setRegistrationMode] = useState<RegistrationMode>('restricted');
  const [requireEmailVerification, setRequireEmailVerification] = useState(true);
  const [sessionTimeoutHours, setSessionTimeoutHours] = useState(168);
  const [maxUsers, setMaxUsers] = useState<number | null>(null);
  const [newDomain, setNewDomain] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<AdminSettingsTab>('registration');
  
  // Email configuration
  const { data: emailConfig, isLoading: emailLoading, refetch: refetchEmail } = useEmailConfig();
  const updateEmailConfig = useUpdateEmailConfig();
  const sendTestEmail = useSendTestEmail();
  const [resendApiKey, setResendApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [emailSenderDomain, setEmailSenderDomain] = useState('');
  const [emailSenderName, setEmailSenderName] = useState('');
  const [emailVerificationEnabled, setEmailVerificationEnabled] = useState(false);
  const [hasEmailChanges, setHasEmailChanges] = useState(false);

  // MCP token management
  const { data: mcpTokens, isLoading: mcpTokensLoading, refetch: refetchMcpTokens, isFetching: mcpTokensFetching } = useMcpTokens();
  const createMcpToken = useCreateMcpToken();
  const revokeMcpToken = useRevokeMcpToken();
  const [mcpTokenName, setMcpTokenName] = useState('OpenCode');
  const [newMcpToken, setNewMcpToken] = useState('');

  // Load preferences into form
  useEffect(() => {
    if (systemPrefs) {
      setRegistrationMode(systemPrefs.registrationMode as RegistrationMode);
      setRequireEmailVerification(systemPrefs.requireEmailVerification);
      setSessionTimeoutHours(systemPrefs.sessionTimeoutHours);
      setMaxUsers(systemPrefs.maxUsers);
      setHasChanges(false);
    }
  }, [systemPrefs]);

  // Track changes
  useEffect(() => {
    if (systemPrefs) {
      const changed = 
        registrationMode !== systemPrefs.registrationMode ||
        requireEmailVerification !== systemPrefs.requireEmailVerification ||
        sessionTimeoutHours !== systemPrefs.sessionTimeoutHours ||
        maxUsers !== systemPrefs.maxUsers;
      setHasChanges(changed);
    }
  }, [registrationMode, requireEmailVerification, sessionTimeoutHours, maxUsers, systemPrefs]);

  // Load email config into form
  useEffect(() => {
    if (emailConfig) {
      setEmailSenderDomain(emailConfig.emailSenderDomain);
      setEmailSenderName(emailConfig.emailSenderName);
      setEmailVerificationEnabled(emailConfig.emailVerificationEnabled);
      setResendApiKey(''); // Never load the actual key
      setHasEmailChanges(false);
    }
  }, [emailConfig]);

  // Track email changes
  useEffect(() => {
    if (emailConfig) {
      const changed = 
        emailSenderDomain !== emailConfig.emailSenderDomain ||
        emailSenderName !== emailConfig.emailSenderName ||
        emailVerificationEnabled !== emailConfig.emailVerificationEnabled ||
        resendApiKey.length > 0; // Any new API key is a change
      setHasEmailChanges(changed);
    }
  }, [emailSenderDomain, emailSenderName, emailVerificationEnabled, resendApiKey, emailConfig]);

  const handleSaveSettings = async () => {
    try {
      await updatePreferences.mutateAsync({
        registrationMode,
        requireEmailVerification,
        sessionTimeoutHours,
        maxUsers,
      });
      toast.success('System settings saved');
      setHasChanges(false);
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;
    
    try {
      await addDomain.mutateAsync(newDomain.trim().toLowerCase());
      toast.success(`Domain ${newDomain} added`);
      setNewDomain('');
    } catch (err) {
      toast.error('Failed to add domain');
    }
  };

  const handleRemoveDomain = async (domain: string) => {
    if (!confirm(`Remove ${domain} from approved domains?`)) return;
    
    try {
      await removeDomain.mutateAsync(domain);
      toast.success(`Domain ${domain} removed`);
    } catch (err) {
      toast.error('Failed to remove domain');
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'admin' | 'user') => {
    if (userId === user?.id && newRole === 'user') {
      toast.error('You cannot demote yourself');
      return;
    }
    
    try {
      await updateRole.mutateAsync({ userId, role: newRole });
      toast.success('User role updated');
    } catch (err) {
      toast.error('Failed to update role');
    }
  };

  const handleSaveEmailConfig = async () => {
    try {
      const updateData: {
        resendApiKey?: string | null;
        emailSenderDomain?: string;
        emailSenderName?: string;
        emailVerificationEnabled?: boolean;
      } = {};
      
      if (resendApiKey.length > 0) {
        updateData.resendApiKey = resendApiKey;
      }
      if (emailSenderDomain !== emailConfig?.emailSenderDomain) {
        updateData.emailSenderDomain = emailSenderDomain;
      }
      if (emailSenderName !== emailConfig?.emailSenderName) {
        updateData.emailSenderName = emailSenderName;
      }
      if (emailVerificationEnabled !== emailConfig?.emailVerificationEnabled) {
        updateData.emailVerificationEnabled = emailVerificationEnabled;
      }
      
      await updateEmailConfig.mutateAsync(updateData);
      toast.success('Email configuration saved');
      setResendApiKey('');
      setHasEmailChanges(false);
      refetchEmail();
    } catch (err) {
      toast.error('Failed to save email configuration');
    }
  };

  const handleSendTestEmail = async () => {
    try {
      await sendTestEmail.mutateAsync();
      toast.success('Test email sent! Check your inbox.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send test email');
    }
  };

  const mcpUrl = resolveMcpUrl();
  const opencodeConfig = JSON.stringify({
    $schema: 'https://opencode.ai/config.json',
    mcp: {
      sbclite: {
        type: 'remote',
        url: mcpUrl,
        enabled: true,
        headers: {
          Authorization: 'Bearer {env:SBCLITE_MCP_TOKEN}',
        },
      },
    },
  }, null, 2);

  const mcpAgentInstructions = `When debugging SBCLite production/runtime behavior, use the sbclite MCP tools before asking the user to inspect logs manually.

Preferred flow:
1. Use sbclite.find_recent_errors for recent backend or sip-engine failures.
2. Use sbclite.search_logs with service, search, or callId-related terms for application context.
3. Use sbclite.list_sip_call_events with the SIP Call-ID for protocol-level INVITE, REGISTER, OPTIONS, BYE, and response traces.
4. Cross-check application logs and SIP traffic before proposing SIP routing, authentication, media, or transport fixes.`;

  const handleCreateMcpToken = async () => {
    try {
      const result = await createMcpToken.mutateAsync({ name: mcpTokenName.trim() || 'OpenCode' });
      setNewMcpToken(result.token);
      toast.success('MCP token created. Copy it now; it will not be shown again.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create MCP token');
    }
  };

  const handleRevokeMcpToken = async (id: string) => {
    if (!confirm('Revoke this MCP token? Agents using it will lose access immediately.')) return;

    try {
      await revokeMcpToken.mutateAsync(id);
      toast.success('MCP token revoked');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to revoke MCP token');
    }
  };

  const copyToClipboard = async (value: string, message: string) => {
    await navigator.clipboard.writeText(value);
    toast.success(message);
  };

  if (prefsLoading) {
    return <LoadingState />;
  }

  if (prefsError) {
    return (
      <ErrorState
        title="Failed to load system settings"
        message={prefsError instanceof Error ? prefsError.message : 'An unexpected error occurred'}
        onRetry={() => refetch()}
      />
    );
  }

  // Calculate statistics
  const stats = {
    totalUsers: users?.length || 0,
    admins: users?.filter(u => u.role === 'admin').length || 0,
    verified: users?.filter(u => u.emailVerified).length || 0,
    domains: systemPrefs?.approvedDomains.length || 0
  };

  // Access control — must come after all hooks so hook order stays stable across renders.
  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <Shield className="w-12 h-12 mx-auto mb-2" />
          <p className="text-lg font-medium">Access Denied</p>
          <p className="text-sm text-gray-600 mt-1">
            You need administrator privileges to access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="System Settings"
        description="Manage system-wide configuration and users"
        onRefresh={() => refetch()}
        isRefreshing={isFetching}
      />

      {/* Statistics Cards */}
      <StatsGrid>
        <StatCard
          label="Total Users"
          value={stats.totalUsers}
          icon={Users}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          label="Administrators"
          value={stats.admins}
          icon={Shield}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
          valueColor="text-purple-600"
        />
        <StatCard
          label="Verified Users"
          value={stats.verified}
          icon={CheckCircle}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
          valueColor="text-green-600"
        />
        <StatCard
          label="Approved Domains"
          value={stats.domains}
          icon={Globe}
          iconBgColor="bg-orange-100"
          iconColor="text-orange-600"
          valueColor="text-orange-600"
        />
      </StatsGrid>

      {/* Tab Navigation */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('registration')}
            className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'registration'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Settings className="w-4 h-4" />
            <span>Registration Settings</span>
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'email'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Mail className="w-4 h-4" />
            <span>Email</span>
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>User Management</span>
          </button>
          <button
            onClick={() => setActiveTab('mcp')}
            className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'mcp'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Bot className="w-4 h-4" />
            <span>MCP / Agents</span>
          </button>

        </nav>
      </div>

      {/* Registration Settings Tab */}
      {activeTab === 'registration' && (
        <div className="space-y-6">
          {/* Registration Mode */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Registration Mode</h2>
            </div>
            <div className="px-6 py-6 space-y-4">
              {[
                { value: 'open', label: 'Open', description: 'Anyone can register' },
                { value: 'restricted', label: 'Restricted', description: 'Only approved domains can register' },
                { value: 'invite_only', label: 'Invite Only', description: 'Registration is disabled' },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                    registrationMode === option.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="registrationMode"
                    value={option.value}
                    checked={registrationMode === option.value}
                    onChange={(e) => setRegistrationMode(e.target.value as RegistrationMode)}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="ml-3">
                    <span className="block text-sm font-medium text-gray-900">
                      {option.label}
                    </span>
                    <span className="block text-sm text-gray-500">
                      {option.description}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Approved Domains */}
          {registrationMode === 'restricted' && (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Approved Domains</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Only users with these email domains can register
                </p>
              </div>
              <div className="px-6 py-4">
                {/* Add Domain Form */}
                <div className="flex space-x-3 mb-4">
                  <input
                    type="text"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    placeholder="example.com"
                    className="flex-1 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleAddDomain}
                    disabled={!newDomain.trim() || addDomain.isPending}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4" />
                    {addDomain.isPending ? 'Adding...' : 'Add Domain'}
                  </button>
                </div>

                {/* Domain List */}
                <div className="space-y-2">
                  {systemPrefs?.approvedDomains.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No approved domains configured</p>
                  ) : (
                    systemPrefs?.approvedDomains.map((domain) => (
                      <div
                        key={domain}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-700">{domain}</span>
                        </div>
                        <button
                          onClick={() => handleRemoveDomain(domain)}
                          disabled={removeDomain.isPending}
                          className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          <X className="w-4 h-4" />
                          Remove
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Other Settings */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Other Settings</h2>
            </div>
            <div className="px-6 py-6 space-y-6">
              {/* Email Verification */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Require Email Verification
                  </label>
                  <p className="text-sm text-gray-500">
                    Users must verify their email before accessing the system
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={requireEmailVerification}
                  onClick={() => setRequireEmailVerification(!requireEmailVerification)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    requireEmailVerification ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      requireEmailVerification ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Session Timeout */}
              <div>
                <label htmlFor="sessionTimeout" className="block text-sm font-medium text-gray-700 mb-2">
                  Session Timeout (hours)
                </label>
                <input
                  type="number"
                  id="sessionTimeout"
                  min={1}
                  max={8760}
                  value={sessionTimeoutHours}
                  onChange={(e) => setSessionTimeoutHours(Number(e.target.value))}
                  className="block w-32 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  How long users stay logged in (1-8760 hours)
                </p>
              </div>

              {/* Max Users */}
              <div>
                <label htmlFor="maxUsers" className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Users
                </label>
                <input
                  type="number"
                  id="maxUsers"
                  min={1}
                  value={maxUsers ?? ''}
                  onChange={(e) => setMaxUsers(e.target.value ? Number(e.target.value) : null)}
                  placeholder="Unlimited"
                  className="block w-32 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Leave empty for unlimited users
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end rounded-b-lg">
              <button
                type="button"
                onClick={handleSaveSettings}
                disabled={!hasChanges || updatePreferences.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updatePreferences.isPending ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Tab */}
      {activeTab === 'email' && (
        <div className="space-y-6">
          {emailLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Email Provider Status */}
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Email Provider Status</h2>
                </div>
                <div className="px-6 py-4">
                  <div className="flex items-center gap-4">
                    {emailConfig?.resendApiKeyConfigured ? (
                      <>
                        <div className="p-2 bg-green-100 rounded-lg">
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Resend is configured</p>
                          <p className="text-sm text-gray-500">
                            API Key: {emailConfig.resendApiKeyMasked}
                            {emailConfig.resendEnvLocked && (
                              <span className="ml-2 inline-flex items-center gap-1 text-xs text-orange-600">
                                <Lock className="w-3 h-3" />
                                Set via environment variable
                              </span>
                            )}
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="p-2 bg-yellow-100 rounded-lg">
                          <AlertCircle className="w-6 h-6 text-yellow-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Resend is not configured</p>
                          <p className="text-sm text-gray-500">
                            Email verification and notifications are disabled
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Resend API Key */}
              {!emailConfig?.resendEnvLocked && (
                <div className="bg-white rounded-lg border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Resend API Key</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Get your API key from <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">resend.com</a>
                    </p>
                  </div>
                  <div className="px-6 py-4">
                    <div className="relative">
                      <input
                        type={showApiKey ? 'text' : 'password'}
                        value={resendApiKey}
                        onChange={(e) => setResendApiKey(e.target.value)}
                        placeholder={emailConfig?.resendApiKeyConfigured ? 'Enter new API key to replace current' : 'Enter your Resend API key'}
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showApiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Sender Configuration */}
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Sender Configuration</h2>
                </div>
                <div className="px-6 py-6 space-y-4">
                  <div>
                    <label htmlFor="emailSenderName" className="block text-sm font-medium text-gray-700 mb-2">
                      Sender Name
                    </label>
                    <input
                      type="text"
                      id="emailSenderName"
                      value={emailSenderName}
                      onChange={(e) => setEmailSenderName(e.target.value)}
                      placeholder="SBCLite"
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      The name that appears in the "From" field of emails
                    </p>
                  </div>
                  <div>
                    <label htmlFor="emailSenderDomain" className="block text-sm font-medium text-gray-700 mb-2">
                      Sender Domain
                    </label>
                    <input
                      type="text"
                      id="emailSenderDomain"
                      value={emailSenderDomain}
                      onChange={(e) => setEmailSenderDomain(e.target.value)}
                      placeholder="sbclite.local"
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      The domain used for sending emails (must be verified in Resend)
                    </p>
                  </div>
                </div>
              </div>

              {/* Email Verification Setting */}
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Email Verification</h2>
                </div>
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Enable Email Verification
                      </label>
                      <p className="text-sm text-gray-500">
                        Require new users to verify their email address
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={emailVerificationEnabled}
                      onClick={() => setEmailVerificationEnabled(!emailVerificationEnabled)}
                      disabled={!emailConfig?.resendApiKeyConfigured}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        emailVerificationEnabled ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          emailVerificationEnabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                  {!emailConfig?.resendApiKeyConfigured && (
                    <p className="mt-2 text-sm text-yellow-600">
                      Configure Resend API key to enable email verification
                    </p>
                  )}
                </div>
              </div>

              {/* Test Email */}
              {emailConfig?.resendApiKeyConfigured && (
                <div className="bg-white rounded-lg border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Test Configuration</h2>
                  </div>
                  <div className="px-6 py-4">
                    <p className="text-sm text-gray-500 mb-4">
                      Send a test email to your email address ({user?.email}) to verify the configuration
                    </p>
                    <button
                      onClick={handleSendTestEmail}
                      disabled={sendTestEmail.isPending}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="w-4 h-4" />
                      {sendTestEmail.isPending ? 'Sending...' : 'Send Test Email'}
                    </button>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveEmailConfig}
                  disabled={!hasEmailChanges || updateEmailConfig.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateEmailConfig.isPending ? 'Saving...' : 'Save Email Settings'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* MCP / Agents Tab */}
      {activeTab === 'mcp' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Remote MCP</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Give trusted agents read-only access to SBCLite application logs and SIP traffic.
                </p>
              </div>
              <button
                type="button"
                onClick={() => refetchMcpTokens()}
                disabled={mcpTokensFetching}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${mcpTokensFetching ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
            <div className="px-6 py-6 space-y-6">
              <div>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">Endpoint</p>
                    <p className="mt-1 text-sm font-mono text-gray-600 break-all">{mcpUrl}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(mcpUrl, 'MCP endpoint copied')}
                    className="shrink-0 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    <Copy className="w-4 h-4" />
                    Copy
                  </button>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                <div>
                  <label htmlFor="mcpTokenName" className="block text-sm font-medium text-gray-700 mb-2">
                    New token name
                  </label>
                  <input
                    id="mcpTokenName"
                    type="text"
                    value={mcpTokenName}
                    onChange={(event) => setMcpTokenName(event.target.value)}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="OpenCode"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={handleCreateMcpToken}
                    disabled={createMcpToken.isPending}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Key className="w-4 h-4" />
                    {createMcpToken.isPending ? 'Creating...' : 'Create Token'}
                  </button>
                </div>
              </div>

              {newMcpToken && (
                <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4">
                  <p className="text-sm font-medium text-yellow-900">Copy this token now. It will not be shown again.</p>
                  <div className="mt-3 flex items-center gap-2">
                    <code className="min-w-0 flex-1 truncate rounded-md border border-yellow-200 bg-white px-3 py-2 text-sm font-mono text-gray-800">
                      {newMcpToken}
                    </code>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(newMcpToken, 'MCP token copied')}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 border border-yellow-300 rounded-md hover:bg-yellow-100"
                    >
                      <Copy className="w-4 h-4" />
                      Copy
                    </button>
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-gray-900 mb-2">Tokens</p>
                {mcpTokensLoading ? (
                  <p className="text-sm text-gray-500">Loading tokens...</p>
                ) : mcpTokens?.length ? (
                  <div className="space-y-2">
                    {mcpTokens.map((token) => (
                      <div key={token.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{token.name}</p>
                          <p className="text-xs text-gray-500">
                            <span className="font-mono">{token.tokenPrefix}...</span>
                            <span className="mx-1">-</span>
                            Created {new Date(token.createdAt).toLocaleString()} by {token.createdByEmail}
                            {token.revokedAt ? ` - Revoked ${new Date(token.revokedAt).toLocaleString()}` : ''}
                          </p>
                        </div>
                        {!token.revokedAt && (
                          <button
                            type="button"
                            onClick={() => handleRevokeMcpToken(token.id)}
                            disabled={revokeMcpToken.isPending}
                            className="shrink-0 px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50"
                          >
                            Revoke
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No MCP tokens created.</p>
                )}
              </div>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">OpenCode Config</h2>
                <p className="text-sm text-gray-500 mt-1">Use the token through an environment variable, not inline in config.</p>
              </div>
              <div className="px-6 py-4">
                <pre className="max-h-80 overflow-auto rounded-lg bg-gray-900 p-4 text-xs text-gray-100">{opencodeConfig}</pre>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Agent Instructions</h2>
                <p className="text-sm text-gray-500 mt-1">Add this to project-local agent guidance for SBCLite debugging.</p>
              </div>
              <div className="px-6 py-4">
                <pre className="max-h-80 overflow-auto rounded-lg bg-gray-50 p-4 text-xs text-gray-800 whitespace-pre-wrap">{mcpAgentInstructions}</pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Management Tab */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Users</h2>
            <p className="text-sm text-gray-500 mt-1">
              Manage user accounts and roles
            </p>
          </div>
          <div className="overflow-x-auto">
            {usersLoading ? (
              <div className="p-6 text-center text-gray-500">Loading users...</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email Verified
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users?.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{u.name}</div>
                          <div className="text-sm text-gray-500">{u.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          u.emailVerified 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {u.emailVerified ? 'Verified' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={u.role}
                          onChange={(e) => handleRoleChange(u.id, e.target.value as 'admin' | 'user')}
                          disabled={u.id === user?.id || updateRole.isPending}
                          className="text-sm rounded-md border border-gray-300 px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                        {u.id === user?.id && (
                          <span className="ml-2 text-xs text-gray-500">(you)</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(u.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSettingsPage;
